/* eslint-disable @typescript-eslint/no-explicit-any */
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import _ from "lodash";
import { isPlatform } from "@ionic/react";
import { useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router";
import { EventRegister } from "react-native-event-listeners";
import * as FirebaseService from "@controllers/FirebaseService";
import { useLearningLoop } from "@hooks/programs/useLearningLoop";
import { learningLoopDeeplinkDataAtom } from "@state/TimelineState";
import { useSetRecoilState } from "recoil";
import {
  INewDeepLinkEvidence,
  INewDeepLinkRenfrewEvidence,
  IParapassQuizData,
  IUser,
  ParapassPDFRequestParams,
} from "@interfaces/Interfaces";
import { useLoggedIn } from "@hooks/auth/useLoggedIn";
import { format } from "date-fns";
import * as DatabaseService from "@controllers/DatabaseService";
import { blobToBase64 } from "@utils/parapassUtils";
import { useRefreshParapassData } from "@hooks/data/useRefreshParapassData";
import { Preferences } from "@capacitor/preferences";
import { WEB_HOSTS } from "@environment/env";

const DeepLinkingListener: React.FC = () => {
  const interval = useRef<any>(null);
  const processedUrlRef = useRef<string | null>(null);

  const { hasLearningLoop } = useLearningLoop();
  const isLoggedIn = useLoggedIn();
  const history = useHistory();

  const setLearningLoopDeeplinkData = useSetRecoilState(learningLoopDeeplinkDataAtom);
  const { refreshParapassQuizData } = useRefreshParapassData();

  const waitForUser = async (): Promise<void> => {
    return new Promise((resolve) => {
      const active = document.getElementById("deep-linking-active");

      if (active) {
        resolve();
        return;
      }

      interval.current = setInterval(() => {
        const active = document.getElementById("deep-linking-active");

        if (active) {
          clearInterval(interval.current);

          setTimeout(() => {
            resolve();
          }, 500);
        }
      }, 500);
    });
  };

  const handleAppUrlOpen = useCallback(
    async (url: string): Promise<void> => {
      if (processedUrlRef.current === url) {
        return;
      }

      if (!isLoggedIn && !processedUrlRef.current) {
        processedUrlRef.current = url;
        return;
      }

      let path = processedUrlRef.current?.replace(/#\//g, "") ?? url.replace(/#\//g, "");
      path = path.replace(/@/g, "&");

      processedUrlRef.current = null;

      const parsedURL = new URL(path);

      const addReflection = parsedURL.searchParams.get("addreflection");

      const externalURLParam = parsedURL.searchParams.get("externalurl");

      let universalLinkParam = null;

      if (!url.includes("?")) {
        const isUniversalHost = WEB_HOSTS.some((host) => path.includes(host));
        if (isUniversalHost) {
          universalLinkParam = parsedURL.pathname;
        } else if (path.includes("parafolio://")) {
          universalLinkParam = url.replace("parafolio://", "");
        }
      }

      await waitForUser();

      if (universalLinkParam) {
        FirebaseService.logEvent("deep_linking", { type: "universal", path: universalLinkParam });

        history.push(universalLinkParam);
      } else if (externalURLParam) {
        FirebaseService.logEvent("deep_linking", { type: "external", path: externalURLParam });

        Browser.open({ url: externalURLParam, presentationStyle: "popover" });
      } else if (addReflection === "true" && parsedURL.searchParams.get("programmodule") === "renfrew") {
        const title = parsedURL.searchParams.get("title");
        const duration = parsedURL.searchParams.get("duration");
        const evidenceType = parsedURL.searchParams.get("evidencetype");
        const skill = parsedURL.searchParams.get("skill");
        const renfrewEvidence: INewDeepLinkRenfrewEvidence = {
          ...(title && { title }),
          ...(duration && { duration }),
          ...(evidenceType && { evidenceType: evidenceType.toLowerCase() }),
          skill: !skill || skill.toLowerCase() === "null" ? null : skill,
        };

        FirebaseService.logEvent("deep_linking", { type: "add_renfrew_evidence" });
        EventRegister.emit("evidence-modal/add-renfrew-deeplink", renfrewEvidence);
      } else if (addReflection === "true") {
        const title = parsedURL.searchParams.get("title");
        const date = parsedURL.searchParams.get("date");
        const activityDate = parsedURL.searchParams.get("activitydate");
        const activityType = parsedURL.searchParams.get("activitytype");
        const activityDuration = parsedURL.searchParams.get("activityduration");
        const activityDescription = parsedURL.searchParams.get("activitydescription");
        const timelineReference = parsedURL.searchParams.get("timelinereference");
        const reflection = parsedURL.searchParams.get("reflection");
        const benefits = parsedURL.searchParams.get("benefits");
        const learningObjectives = parsedURL.searchParams.get("learningobjectives");

        let evidence = {
          ...(title && { title }),
          ...(date && { date: Number(date) }),
          ...(activityDate && { activityDate: Number(activityDate) }),
          ...(activityType && { activityType }),
          ...(activityDuration && { activityDuration }),
          ...(activityDescription && { activityDescription }),
          ...(timelineReference && { timelineReference }),
          ...(reflection && { reflection }),
          ...(benefits && { benefits }),
          ...(learningObjectives && { learningObjectives }),
        };

        const splitTimelineReference = timelineReference?.split("-");
        const deepLinkUser = JSON.parse((await Preferences.get({ key: "user" })).value ?? "{}") as IUser;

        if (deepLinkUser && splitTimelineReference && splitTimelineReference.length === 2) {
          const updatedParapassQuizData = await refreshParapassQuizData(deepLinkUser);
          const quizSet = splitTimelineReference[0];

          const found = updatedParapassQuizData.find(
            (item: IParapassQuizData) => item.quizSet === quizSet && `${item.date}` === activityDate
          );

          if (found) {
            const totalScore = found.score.correct + found.score.incorrect + found.score.unanswered;

            const params: ParapassPDFRequestParams = {
              contactID: deepLinkUser?.userData?.contactID,
              token: deepLinkUser?.token ?? "",
              date: format(new Date(found.date), "do MMMM yyyy"),
              quizName: found.title,
              correctCount: `${found.score.correct}`,
              questionCount: `${totalScore}`,
              userName: deepLinkUser?.userData?.userName ?? "",
              quizType: found.type,
            };

            let pdfString;
            let pdf;

            try {
              pdf = await DatabaseService.getParapassCertificate(params);
              pdfString = await blobToBase64(pdf);

              evidence = _.extend(evidence, {
                pdfData: {
                  pdf: pdfString,
                  size: pdf?.size,
                  certificateFileName: `ParaPass_Certificate_${format(new Date(found.date), "dd-MMM-yyyy_hh-mm-ss")}.pdf`,
                },
              });
            } catch (error) {
              console.error(error);
            }
          }
        }

        if (hasLearningLoop) {
          FirebaseService.logEvent("deep_linking", { type: "add_learning_loop" });
          setLearningLoopDeeplinkData(evidence as INewDeepLinkEvidence);
          EventRegister.emit("learning-loop/add-evidence-deeplink");
        } else {
          FirebaseService.logEvent("deep_linking", { type: "add_evidence" });
          EventRegister.emit("evidence-modal/add-evidence-deeplink", evidence);
        }
      }
    },
    [hasLearningLoop, history, setLearningLoopDeeplinkData, isLoggedIn]
  );

  useEffect(() => {
    if (isPlatform("desktop") || isPlatform("mobileweb")) {
      const href = window.location.href;
      handleAppUrlOpen(href);
    } else {
      App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
        handleAppUrlOpen(event.url);
      });
    }

    return () => {
      App.removeAllListeners();
    };
  }, [handleAppUrlOpen]);

  return null;
};

export default DeepLinkingListener;
